import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { isAuthenticated } from '@/lib/auth';
import { apiClient } from '@/lib/api-client';

interface WebhookConfig {
  webhookUrl: string | null;
  webhookSecretSet: boolean;
  enabledEvents: string[];
}

interface WebhookStats {
  summary: {
    totalDeliveries: number;
    successfulDeliveries: number;
    failedDeliveries: number;
    successRate: string;
    avgResponseTime: number;
  };
}

interface WebhookLog {
  id: string;
  event: string;
  status: 'PENDING' | 'DELIVERED' | 'FAILED';
  attempts: number;
  lastAttempt: string | null;
  nextRetry: string | null;
  responseCode: number | null;
  responseBody: string | null;
  createdAt: string;
  session: {
    id: string;
    type: string;
    status: string;
  };
  partner: {
    webhookUrl: string | null;
  };
}

interface WebhookLogsResponse {
  logs: WebhookLog[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export default function Webhooks() {
  const navigate = useNavigate();
  const [config, setConfig] = useState<WebhookConfig | null>(null);
  const [stats, setStats] = useState<WebhookStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [newSecret, setNewSecret] = useState<string | null>(null);

  // Form state
  const [webhookUrl, setWebhookUrl] = useState('');
  const [isEditing, setIsEditing] = useState(false);

  // Webhook logs state
  const [logsData, setLogsData] = useState<WebhookLogsResponse | null>(null);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsStatus, setLogsStatus] = useState<string>('');
  const [selectedLog, setSelectedLog] = useState<WebhookLog | null>(null);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }

    loadWebhookConfig();
    loadWebhookStats();
    loadWebhookLogs();
  }, [navigate]);

  useEffect(() => {
    // Reload logs when filters change
    loadWebhookLogs();
  }, [logsPage, logsStatus]);

  const loadWebhookConfig = async () => {
    try {
      const response = await apiClient.get('/webhooks/config');
      setConfig(response.data);
      setWebhookUrl(response.data.webhookUrl || '');
    } catch (error) {
      console.error('Failed to load webhook config:', error);
      setError('Failed to load webhook configuration');
    } finally {
      setLoading(false);
    }
  };

  const loadWebhookStats = async () => {
    try {
      const response = await apiClient.get('/webhooks/stats?days=7');
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load webhook stats:', error);
    }
  };

  const loadWebhookLogs = async () => {
    setLogsLoading(true);
    try {
      const params = new URLSearchParams({
        page: logsPage.toString(),
        limit: '20'
      });

      if (logsStatus) {
        params.append('status', logsStatus);
      }

      const response = await apiClient.get(`/webhooks/logs?${params.toString()}`);
      setLogsData(response.data);
    } catch (error) {
      console.error('Failed to load webhook logs:', error);
    } finally {
      setLogsLoading(false);
    }
  };

  const handleSaveConfig = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await apiClient.put('/webhooks/config', {
        webhookUrl: webhookUrl || null
      });

      setSuccess('Webhook configuration saved successfully');
      setIsEditing(false);
      await loadWebhookConfig();
    } catch (error: any) {
      console.error('Failed to save webhook config:', error);
      setError(error.response?.data?.error || 'Failed to save webhook configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleGenerateSecret = async () => {
    if (!confirm('Generate a new webhook secret? The old secret will be replaced.')) {
      return;
    }

    setGenerating(true);
    setError(null);
    setNewSecret(null);

    try {
      const response = await apiClient.post('/webhooks/generate-secret');
      setNewSecret(response.data.webhookSecret);
      setSuccess('New webhook secret generated successfully');
      await loadWebhookConfig();
    } catch (error: any) {
      console.error('Failed to generate secret:', error);
      setError(error.response?.data?.error || 'Failed to generate webhook secret');
    } finally {
      setGenerating(false);
    }
  };

  const handleTestWebhook = async () => {
    if (!config?.webhookUrl) {
      setError('Please configure a webhook URL first');
      return;
    }

    setTesting(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await apiClient.post('/webhooks/test');
      setSuccess(`Test webhook sent successfully. Status: ${response.data.statusCode}`);
    } catch (error: any) {
      console.error('Failed to test webhook:', error);
      setError(error.response?.data?.error || error.response?.data?.message || 'Failed to send test webhook');
    } finally {
      setTesting(false);
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Copied to clipboard!');
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-96">
          <div className="text-gray-600">Loading webhook configuration...</div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Webhooks</h1>
          <p className="text-gray-600 mt-1">Configure webhook notifications for payment events</p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
            {error}
          </div>
        )}

        {/* Success Alert */}
        {success && (
          <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">
            {success}
          </div>
        )}

        {/* New Secret Display */}
        {newSecret && (
          <Card className="border-blue-500">
            <CardHeader>
              <CardTitle className="text-blue-700">New Webhook Secret Generated</CardTitle>
              <CardDescription>
                Make sure to copy your webhook secret now. You won't be able to see it again!
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Input
                  type="text"
                  value={newSecret}
                  readOnly
                  className="font-mono text-sm"
                />
                <Button onClick={() => copyToClipboard(newSecret)} variant="outline">
                  Copy
                </Button>
              </div>
              <Button
                onClick={() => setNewSecret(null)}
                variant="outline"
                className="mt-4"
              >
                I've saved it securely
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Webhook URL Configuration */}
        <Card>
          <CardHeader>
            <CardTitle>Webhook URL</CardTitle>
            <CardDescription>
              Set the endpoint where you want to receive webhook notifications
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {!isEditing && config?.webhookUrl ? (
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <Input
                    type="text"
                    value={config.webhookUrl}
                    readOnly
                    className="font-mono text-sm"
                  />
                  <Button onClick={() => setIsEditing(true)} variant="outline">
                    Edit
                  </Button>
                </div>
                <Button onClick={handleTestWebhook} disabled={testing} variant="outline">
                  {testing ? 'Sending test...' : 'Send Test Webhook'}
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <Input
                  type="url"
                  value={webhookUrl}
                  onChange={(e) => setWebhookUrl(e.target.value)}
                  placeholder="https://your-domain.com/webhooks/pouchpay"
                  disabled={saving}
                />
                <div className="flex gap-2">
                  <Button onClick={handleSaveConfig} disabled={saving}>
                    {saving ? 'Saving...' : 'Save URL'}
                  </Button>
                  {isEditing && (
                    <Button
                      onClick={() => {
                        setIsEditing(false);
                        setWebhookUrl(config?.webhookUrl || '');
                      }}
                      variant="outline"
                      disabled={saving}
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Webhook Secret */}
        <Card>
          <CardHeader>
            <CardTitle>Webhook Secret</CardTitle>
            <CardDescription>
              Use this secret to verify webhook signatures
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div>
                {config?.webhookSecretSet ? (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-green-600">✓</span>
                    Webhook secret is configured
                  </div>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <span className="text-yellow-600">⚠</span>
                    No webhook secret configured
                  </div>
                )}
              </div>
              <Button onClick={handleGenerateSecret} disabled={generating} variant="outline">
                {generating ? 'Generating...' : config?.webhookSecretSet ? 'Regenerate Secret' : 'Generate Secret'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Webhook Statistics */}
        {stats && (
          <Card>
            <CardHeader>
              <CardTitle>Webhook Statistics (Last 7 Days)</CardTitle>
              <CardDescription>Delivery success and performance metrics</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Total Deliveries</div>
                  <div className="text-2xl font-bold">{stats.summary.totalDeliveries}</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Successful</div>
                  <div className="text-2xl font-bold text-green-600">
                    {stats.summary.successfulDeliveries}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Failed</div>
                  <div className="text-2xl font-bold text-red-600">
                    {stats.summary.failedDeliveries}
                  </div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Success Rate</div>
                  <div className="text-2xl font-bold">{stats.summary.successRate}%</div>
                </div>
                <div>
                  <div className="text-sm text-gray-600 mb-1">Avg Response Time</div>
                  <div className="text-2xl font-bold">{stats.summary.avgResponseTime}ms</div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Webhook Events */}
        <Card>
          <CardHeader>
            <CardTitle>Webhook Events</CardTitle>
            <CardDescription>Events that will trigger webhook notifications</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {config?.enabledEvents.map((event) => (
                <div
                  key={event}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg"
                >
                  <span className="text-green-600">✓</span>
                  <span className="font-mono text-sm">{event}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Webhook Delivery Logs */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Webhook Delivery Logs</CardTitle>
                <CardDescription>History of webhook deliveries sent to your endpoint</CardDescription>
              </div>
              <select
                value={logsStatus}
                onChange={(e) => {
                  setLogsStatus(e.target.value);
                  setLogsPage(1);
                }}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
              >
                <option value="">All Statuses</option>
                <option value="DELIVERED">Delivered</option>
                <option value="FAILED">Failed</option>
                <option value="PENDING">Pending</option>
              </select>
            </div>
          </CardHeader>
          <CardContent>
            {logsLoading ? (
              <div className="text-center py-8 text-gray-600">Loading webhook logs...</div>
            ) : logsData && logsData.logs.length > 0 ? (
              <>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 border-b">
                      <tr>
                        <th className="text-left p-3 font-medium text-gray-700">Event</th>
                        <th className="text-left p-3 font-medium text-gray-700">Webhook URL</th>
                        <th className="text-left p-3 font-medium text-gray-700">Session</th>
                        <th className="text-left p-3 font-medium text-gray-700">Status</th>
                        <th className="text-left p-3 font-medium text-gray-700">Response</th>
                        <th className="text-left p-3 font-medium text-gray-700">Attempts</th>
                        <th className="text-left p-3 font-medium text-gray-700">Created</th>
                        <th className="text-left p-3 font-medium text-gray-700">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {logsData.logs.map((log) => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="p-3">
                            <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                              {log.event}
                            </span>
                          </td>
                          <td className="p-3 max-w-xs">
                            <div className="text-xs text-gray-600 truncate" title={log.partner.webhookUrl || 'N/A'}>
                              {log.partner.webhookUrl ? (
                                <a
                                  href={log.partner.webhookUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-blue-700 underline"
                                >
                                  {log.partner.webhookUrl.length > 40
                                    ? `${log.partner.webhookUrl.substring(0, 40)}...`
                                    : log.partner.webhookUrl}
                                </a>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <div className="text-xs">
                              {log.session ? (
                                <>
                                  <div className="font-mono text-gray-600">{log.session.id.substring(0, 8)}...</div>
                                  <div className="text-gray-500">{log.session.type}</div>
                                </>
                              ) : (
                                <span className="text-gray-400">N/A</span>
                              )}
                            </div>
                          </td>
                          <td className="p-3">
                            <span
                              className={`px-2 py-1 rounded text-xs font-medium ${
                                log.status === 'DELIVERED'
                                  ? 'bg-green-100 text-green-700'
                                  : log.status === 'FAILED'
                                  ? 'bg-red-100 text-red-700'
                                  : 'bg-yellow-100 text-yellow-700'
                              }`}
                            >
                              {log.status}
                            </span>
                          </td>
                          <td className="p-3">
                            {log.responseCode ? (
                              <span
                                className={`text-xs ${
                                  log.responseCode >= 200 && log.responseCode < 300
                                    ? 'text-green-600'
                                    : 'text-red-600'
                                }`}
                              >
                                {log.responseCode}
                              </span>
                            ) : (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </td>
                          <td className="p-3 text-gray-600">{log.attempts}</td>
                          <td className="p-3 text-xs text-gray-600">
                            {new Date(log.createdAt).toLocaleString()}
                          </td>
                          <td className="p-3">
                            <button
                              onClick={() => setSelectedLog(log)}
                              className="text-blue-600 hover:text-blue-700 text-xs font-medium"
                            >
                              View Details
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {logsData.pagination.totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <div className="text-sm text-gray-600">
                      Showing {(logsData.pagination.page - 1) * logsData.pagination.limit + 1} to{' '}
                      {Math.min(
                        logsData.pagination.page * logsData.pagination.limit,
                        logsData.pagination.total
                      )}{' '}
                      of {logsData.pagination.total} logs
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setLogsPage(logsPage - 1)}
                        disabled={logsPage === 1}
                        className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Previous
                      </button>
                      <button
                        onClick={() => setLogsPage(logsPage + 1)}
                        disabled={logsPage === logsData.pagination.totalPages}
                        className="px-3 py-1 border rounded-lg text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                      >
                        Next
                      </button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-8 text-gray-600">
                No webhook logs found. Webhook deliveries will appear here once configured.
              </div>
            )}
          </CardContent>
        </Card>

        {/* Log Detail Modal */}
        {selectedLog && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
            onClick={() => setSelectedLog(null)}
          >
            <div
              className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold">Webhook Delivery Details</h3>
                <button
                  onClick={() => setSelectedLog(null)}
                  className="text-gray-500 hover:text-gray-700 text-xl"
                >
                  ×
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <div className="text-sm text-gray-600 mb-1">Event</div>
                  <div className="font-mono text-sm bg-gray-100 px-3 py-2 rounded">
                    {selectedLog.event}
                  </div>
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-1">Webhook URL</div>
                  {selectedLog.partner.webhookUrl ? (
                    <div className="flex items-center gap-2">
                      <a
                        href={selectedLog.partner.webhookUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="font-mono text-sm bg-gray-100 px-3 py-2 rounded text-blue-600 hover:text-blue-700 underline break-all"
                      >
                        {selectedLog.partner.webhookUrl}
                      </a>
                      <button
                        onClick={() => navigator.clipboard.writeText(selectedLog.partner.webhookUrl!)}
                        className="text-blue-600 hover:text-blue-700 text-xs whitespace-nowrap"
                        title="Copy URL"
                      >
                        Copy
                      </button>
                    </div>
                  ) : (
                    <div className="text-sm text-gray-400">N/A</div>
                  )}
                </div>

                <div>
                  <div className="text-sm text-gray-600 mb-1">Status</div>
                  <span
                    className={`px-3 py-1 rounded text-sm font-medium ${
                      selectedLog.status === 'DELIVERED'
                        ? 'bg-green-100 text-green-700'
                        : selectedLog.status === 'FAILED'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                    }`}
                  >
                    {selectedLog.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Session ID</div>
                    <div className="font-mono text-sm">{selectedLog.session.id}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Session Type</div>
                    <div className="text-sm">{selectedLog.session.type}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Attempts</div>
                    <div className="text-sm">{selectedLog.attempts}</div>
                  </div>
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Response Code</div>
                    <div className="text-sm">{selectedLog.responseCode || 'N/A'}</div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Created At</div>
                    <div className="text-sm">{new Date(selectedLog.createdAt).toLocaleString()}</div>
                  </div>
                  {selectedLog.lastAttempt && (
                    <div>
                      <div className="text-sm text-gray-600 mb-1">Last Attempt</div>
                      <div className="text-sm">
                        {new Date(selectedLog.lastAttempt).toLocaleString()}
                      </div>
                    </div>
                  )}
                </div>

                {selectedLog.nextRetry && (
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Next Retry</div>
                    <div className="text-sm">{new Date(selectedLog.nextRetry).toLocaleString()}</div>
                  </div>
                )}

                {selectedLog.responseBody && (
                  <div>
                    <div className="text-sm text-gray-600 mb-1">Response Body</div>
                    <pre className="bg-gray-100 p-3 rounded text-xs overflow-auto max-h-40">
                      {selectedLog.responseBody}
                    </pre>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end">
                <Button onClick={() => setSelectedLog(null)} variant="outline">
                  Close
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Documentation */}
        <Card>
          <CardHeader>
            <CardTitle>Webhook Implementation Guide</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-gray-600">
            <p>• Webhook notifications are sent as POST requests with JSON payload</p>
            <p>• Verify webhook signatures using the X-Webhook-Signature header and your secret</p>
            <p>• Respond with 2xx status code within 10 seconds to acknowledge receipt</p>
            <p>• Failed webhooks will be retried up to 3 times with exponential backoff</p>
            <p>• Use HTTPS endpoints for security</p>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
